import { CircularProgress, Typography } from '@material-ui/core'
import CssBaseline from '@material-ui/core/CssBaseline'
import React, { useState, useRef, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { Button } from 'src/components/ui/Button'
import { SynRoutes } from 'src/core/consts/synRoutes'
import { SynPerfis } from 'src/core/consts/synPerfis'
import useAuth from 'src/hooks/useAuth'
import { FiArrowLeft, FiLogOut } from 'react-icons/fi'
import { useStyles } from './styles'
import { useCountdown } from './hooks'
import HeaderMfa from 'src/components/mfa/header'
import { CanalDisponivel, MfaEtapa2Response } from 'src/types'
import { showNotificationErrorAPI } from 'src/core/helpers/notification'
import UBox from 'src/components/ui/Ubox'
import LoadingPage from 'src/components/pages/LoadingPage'

export default function MfaEtapa2() {
  const classes = useStyles()
  const history = useHistory()
  const {
    signed,
    perfilAtual,
    storageMfaEtapa2Data,
    storageMfaEtapa1Data,
    validarCodigoMFA,
    enviarCodigoMFA,
    loadingValidacaoCodigoMFA,
    signOut,
    user
  } = useAuth()
  const { seconds, isActive, reset, formatTime } = useCountdown(
    storageMfaEtapa2Data?.canal?.tempoEsperaReenvioSegundos || 60
  )

  const [code, setCode] = useState(['', '', '', ''])

  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const handleInputChange = (index: number, value: string) => {
    if (value.length > 1) return // Evitar múltiplos caracteres

    const newCode = [...code]
    newCode[index] = value
    setCode(newCode)

    // Avançar para o próximo campo se um dígito foi inserido
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Voltar para o campo anterior se estiver vazio
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, 4)
    const newCode = [...code]

    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i]
    }

    setCode(newCode)

    // Focar no último campo preenchido ou no próximo vazio
    const nextEmptyIndex = newCode.findIndex(digit => !digit)
    const focusIndex = nextEmptyIndex === -1 ? 3 : nextEmptyIndex
    inputRefs.current[focusIndex]?.focus()
  }

  const handleSubmit = async () => {
    const fullCode = code.join('')
    if (fullCode.length !== 4) return

    try {
      // Aqui você implementaria a validação do código MFA
      await validarCodigoMFA(fullCode)

      // Se o código for válido, redireciona para o dashboard apropriado
      if (perfilAtual === SynPerfis.SECRETARIA) {
        history.push(SynRoutes.listagemOrcamentos)
      } else {
        history.push(SynRoutes.home)
      }
    } catch (error) {
      showNotificationErrorAPI(error)
      // Limpar código em caso de erro
      setCode(['', '', '', ''])
      inputRefs.current[0]?.focus()
    }
  }

  const handleResendCode = async () => {
    if (!isActive) {
      reset()
      try {
        await enviarCodigoMFA(storageMfaEtapa2Data?.canal)
      } catch (error) {
        showNotificationErrorAPI(error)
      }
    }
  }

  const handleBackToEtapa1 = () => {
    if (!storageMfaEtapa1Data) {
      signOut()
    } else history.push(SynRoutes.mfaEtapa1)
  }

  // Verificar se todos os campos estão preenchidos
  const isCodeComplete = code.every(digit => digit !== '')

  useEffect(() => {
    if (isCodeComplete) {
      handleSubmit()
    }
  }, [isCodeComplete])

  useEffect(() => {
    if (!storageMfaEtapa2Data) {
      if (signed || !!user) {
        history.push(SynRoutes.home)
      } else {
        // signOut()
      }
    }
  }, [storageMfaEtapa2Data, signed, user])

  if (signed || !storageMfaEtapa2Data) {
    return <LoadingPage />
  }

  return (
    <div className="fadeInTransition syn-container-out">
      <CssBaseline />

      {/* Header */}
      <HeaderMfa />
      {/* Main Content */}
      <div className={classes.mfaBox}>
        {/* Botão Voltar */}
        <div className={classes.backButton} onClick={handleBackToEtapa1}>
          <FiArrowLeft />
          Voltar
        </div>

        {/* Título */}
        <Typography variant="h5" color="primary">
          <strong>Confirmação de Código</strong>
        </Typography>

        {/* Ilustração */}
        <UBox mt={3} mb={2}>
          <img
            src="./two-authentication-amico.png"
            alt="MFA Etapa 2"
            style={{ maxWidth: '200px' }}
          />
        </UBox>

        {/* Instrução */}
        <UBox mb={2}>
          <Typography
            variant="body1"
            color="primary"
            style={{ textAlign: 'center' }}
          >
            Código enviado para{' '}
            <b>
              {storageMfaEtapa2Data?.canal?.contatoAnonimizado} via{' '}
              {storageMfaEtapa2Data?.canal?.canalDescricao}
            </b>
            . <br />
            Por favor, informe o código recebido
          </Typography>
        </UBox>

        {/* Campos de Código */}
        <div className={classes.codeInputsContainer}>
          {code.map((digit, index) => (
            <input
              key={index}
              ref={el => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={digit}
              onChange={e => handleInputChange(index, e.target.value)}
              onKeyDown={e => handleKeyDown(index, e)}
              onPaste={handlePaste}
              className={classes.codeInput}
              maxLength={1}
              disabled={loadingValidacaoCodigoMFA}
            />
          ))}
          {loadingValidacaoCodigoMFA && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CircularProgress size={24} />
            </div>
          )}
        </div>

        {/* Link de Reenvio */}
        <div>
          <Typography
            variant="body1"
            className={`${classes.resendLink} ${
              isActive || loadingValidacaoCodigoMFA ? 'disabled' : ''
            }`}
            onClick={handleResendCode}
          >
            Reenviar código
          </Typography>
          {isActive && !loadingValidacaoCodigoMFA && (
            <div className={classes.countdown}>
              Aguarde{' '}
              <span className={classes.countdownHighlight}>{formatTime()}</span>{' '}
              antes de solicitar outro código.
            </div>
          )}
          <Typography variant="body2" style={{ color: '#555' }}>
            Se o código não chegou, toque em <b>"Reenviar código"</b>
          </Typography>
        </div>

        {/* Contador */}

        {/* Botão de Envio */}
        {/* <Button
          onClick={handleSubmit}
          disabled={!isCodeComplete || loading}
          className={classes.submitButton}
        >
          {loading ? 'Verificando...' : 'Confirmar Código'}
        </Button> */}
      </div>
    </div>
  )
}
